import React, { useState, useMemo, useEffect } from 'react';
import { Modal, Box, Chip, TextField, InputAdornment, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { calculateVatAmount, roundVatToInteger } from "../../Utils/GetVatValue";
import { beautifyNumber } from "../../Utils/FormatNumber";
import { PCNCodeNameKeys } from "../../Utils/PCN874Constants";
import { computeUniformVatGroupIds, collapseGroupedSameVatDocs } from "../../Utils/pcnGrouping";
import { translateShaamMessage } from "../../Utils/shaamMessages";
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PCNReportDetailsModal from './PCNReportDetailsModal';
import pcnStyles from './PCNXtram.module.scss';

/** Resolve sort code label like EditModal: sortCodeId - sortCodeName */
const getSortCodeLabel = (sortCodeId, sortCodeIdOptions) => {
  if (!sortCodeId) return null;
  const option = (sortCodeIdOptions || []).find(opt => String(opt.sortCodeId) === String(sortCodeId));
  return option ? `${option.sortCodeId} - ${option.sortCodeName ?? ''}` : sortCodeId;
};

/** Get display type (S, L, M, C, etc.) for a doc for Correct Inputs/Outputs list. */
const getDocDisplayType = (doc, pcnReportDetails) => {
  if (!pcnReportDetails) return doc.documentType || '-';
  if (doc.documentType === 'Incomes') return doc.pcnCompositeType === 'C+M' ? 'M' : (pcnReportDetails.incomeType?.[doc.id] ?? pcnReportDetails.incomeType?.[doc.Id] ?? '-');
  return doc.pcnCompositeType === 'C+M' ? 'C' : (pcnReportDetails.expenseType?.[doc.id] ?? pcnReportDetails.expenseType?.[doc.Id] ?? '-');
};

/** PCN numeric fields: last maxLen digits from the right; empty/all-zeros → "0". */
const normalizePcnNumericField = (val, maxLen = 9) => {
  const s = String(val ?? '').trim();
  if (!s) return '0';
  const truncated = s.length > maxLen ? s.slice(-maxLen) : s;
  return truncated.replace(/^0+/, '') || '0';
};

const normalizeReference = (ref) => normalizePcnNumericField(ref, 9);

const amountClose = (a, b, tolerance = 1) => Math.abs(Number(a) - Number(b)) <= tolerance;

const getDocPcnAmounts = (doc) => {
  if (doc.displayVatAmount != null && doc.displayAmountBeforeVat != null) {
    const vat = Math.abs(Number(doc.displayVatAmount));
    const net = Math.abs(Number(doc.displayAmountBeforeVat));
    return { vat, net, gross: vat + net };
  }
  const vat = Math.abs(roundVatToInteger(calculateVatAmount(doc.amount, doc.vat, doc.documentDate)));
  const net = Math.abs(Number(doc.amount ?? 0) - vat);
  return { vat, net, gross: vat + net };
};

const getInvPcnAmounts = (inv) => {
  const net = Math.abs(Number(inv.invoice_sum ?? 0));
  const vat = Math.abs(Number(inv.invoice_vat ?? 0));
  return { vat, net, gross: vat + net };
};

const formatPcnReferenceDisplay = (ref) => {
  const norm = normalizeReference(ref);
  return norm === '0' ? '000000000' : norm.padStart(9, '0');
};

const docsForIncorrectSection = (inv, allDocs) => {
  if (inv._section === 'output') {
    return allDocs.filter(d => d.documentType === 'Incomes');
  }
  if (inv._section === 'input') {
    return allDocs.filter(d => d.documentType === 'Expenses' || d.documentType === 'EquipmentTransactions');
  }
  return allDocs;
};

const normalizeDateValue = (val) => {
  if (!val) return '';
  const d = new Date(val);
  return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
};

const scoreDocMatch = (doc, inv, pcnReportDetails) => {
  const invRef = normalizeReference(inv.invoice_reference_number);
  const docRef = normalizeReference(doc.reference);
  if (invRef !== docRef) return -1;

  let score = 10;
  const invVat = normalizePcnNumericField(inv.invoice_customer_vat_number, 9);
  const docVat = normalizePcnNumericField(doc.accountCaseId ?? doc.caseId, 9);
  if (invVat === docVat) score += 20;

  const invDate = normalizeDateValue(inv.invoice_date);
  const docDate = normalizeDateValue(doc.documentDate);
  if (invDate && docDate && invDate === docDate) score += 15;

  if (inv.invoice_type) {
    const docType = getDocDisplayType(doc, pcnReportDetails);
    if (docType === inv.invoice_type) score += 10;
  }

  const invAmt = getInvPcnAmounts(inv);
  const docAmt = getDocPcnAmounts(doc);
  if (amountClose(invAmt.net, docAmt.net)) score += 30;
  if (amountClose(invAmt.vat, docAmt.vat)) score += 25;
  if (amountClose(invAmt.gross, docAmt.gross)) score += 15;

  return score;
};

const findMatchingPcnDoc = (inv, allDocs, pcnReportDetails) => {
  const pools = [
    docsForIncorrectSection(inv, allDocs),
    allDocs,
  ];
  for (const pool of pools) {
    let best = null;
    let bestScore = -1;
    for (const doc of pool) {
      const s = scoreDocMatch(doc, inv, pcnReportDetails);
      if (s > bestScore) {
        bestScore = s;
        best = doc;
      }
    }
    if (best && bestScore >= 10) return best;
  }
  return null;
};

/** Resolve account (debit/credit) label like EditModal: cardId - name - accountCaseId (or cardId - name) */
const getAccountLabel = (cardId, accountCardOptions) => {
  if (cardId == null || cardId === '') return null;
  const idStr = String(cardId);
  const options = (accountCardOptions || []).filter(opt => opt.value1 !== 'AddItem' && (opt.cardId != null || opt.CardId != null));
  const option = options.find(opt => {
    const cid = opt.cardId ?? opt.CardId;
    return cid != null && String(cid) === idStr;
  });
  if (!option) return idStr;
  const cid = option.cardId ?? option.CardId ?? '';
  const name = option.name ?? option.Name ?? '';
  const accountCaseId = option.accountCaseId ?? option.AccountCaseId ?? '';
  if (accountCaseId) return `${cid} - ${name} - ${accountCaseId}`;
  return `${cid} - ${name}`;
};

// PCN Checks Accordion Component
const PCNChecksAccordion = ({ pcnHighConfirmationDocs, pcnDocumentsNeedingAllocation, pcnDocumentsWithoutReference, pcnDocumentsOutOfRange, pcnReportDetails, onDocumentClick, vatReportResult, t, direction, pcnCheckStartDate, pcnCheckEndDate, sortCodeIdOptions = [], accountCardOptions = [] }) => {
    const [expandedSections, setExpandedSections] = useState({ vatReportErrors: true });
    const [showPCNReportDetailsModal, setShowPCNReportDetailsModal] = useState(false);
    const [selectedIncorrectInvoice, setSelectedIncorrectInvoice] = useState(null);

    useEffect(() => {
      if (vatReportResult?.report_information || vatReportResult?.reportInformation) {
        setExpandedSections(prev => ({ ...prev, vatReportErrors: true }));
      }
    }, [vatReportResult]);

    const toggleSection = (section) => {
      setExpandedSections(prev => ({
        ...prev,
        [section]: !prev[section]
      }));
    };

    const hasHighConfirmationDocs = pcnHighConfirmationDocs && pcnHighConfirmationDocs.length > 0;
    const hasDocumentsNeedingAllocation = pcnDocumentsNeedingAllocation && pcnDocumentsNeedingAllocation.length > 0;
    const hasDocumentsWithoutReference = pcnDocumentsWithoutReference && pcnDocumentsWithoutReference.length > 0;
    const hasDocumentsOutOfRange = pcnDocumentsOutOfRange && pcnDocumentsOutOfRange.length > 0;
    const hasPcnReportDetails = pcnReportDetails && (pcnReportDetails.incomes?.length > 0 || pcnReportDetails.expenses?.length > 0 || pcnReportDetails.equipmentTransactions?.length > 0);

    if (!hasHighConfirmationDocs && !hasDocumentsNeedingAllocation && !hasDocumentsWithoutReference && !hasDocumentsOutOfRange && !hasPcnReportDetails) {
      return (
        <div style={{
          textAlign: "center",
          padding: "40px 20px",
          color: "#6c757d",
        }}>
          <h3 style={{ margin: "16px 0 8px 0", color: "#495057" }}>
            {t("NoDocumentsRequiringConfirmation")}
          </h3>
          <p>{t("AllDocumentsHaveConfirmationNumbers")}</p>
        </div>
      );
    }
  
    const ChevronIcon = direction === 'rtl' ? ChevronLeftIcon : ChevronRightIcon;

    return (
      <div className={pcnStyles.stack}>
        {hasPcnReportDetails && (
          <button type="button" className={pcnStyles.detailsEntry} onClick={() => setShowPCNReportDetailsModal(true)}>
            <span className={pcnStyles.detailsEntryIcon}><ReceiptLongOutlinedIcon /></span>
            <span className={pcnStyles.detailsEntryText}>
              <strong>{t('PCNReportDetails', { clientName: pcnReportDetails.clientName || '' })}</strong>
              <span>{t('PCNReportDetailsHint')}</span>
            </span>
            <ChevronIcon className={pcnStyles.detailsEntryArrow} />
          </button>
        )}

        {/* PCN Report Details Modal */}
        <PCNReportDetailsModal
          open={showPCNReportDetailsModal}
          onClose={() => setShowPCNReportDetailsModal(false)}
          pcnReportDetails={pcnReportDetails}
          onDocumentClick={onDocumentClick}
          direction={direction}
        />

        {/* VAT Report Errors Section */}
        <VatReportErrorsSection
          vatReportResult={vatReportResult}
          pcnReportDetails={pcnReportDetails}
          expanded={expandedSections.vatReportErrors}
          onToggle={() => toggleSection('vatReportErrors')}
          onInvoiceClick={onDocumentClick}
          t={t}
          direction={direction}
          reportStartDate={pcnCheckStartDate}
          reportEndDate={pcnCheckEndDate}
          sortCodeIdOptions={sortCodeIdOptions}
          accountCardOptions={accountCardOptions}
        />

        {/* Incorrect Invoice Detail Modal */}
        <IncorrectInvoiceDetailModal
          open={!!selectedIncorrectInvoice}
          onClose={() => setSelectedIncorrectInvoice(null)}
          invoice={selectedIncorrectInvoice}
          t={t}
          direction={direction}
        />

      </div>
    );
  };

// ── Invoice type code → chip color ──────────────────────────────────────────────
const invoiceTypeChipColor = (code) => {
  switch (code) {
    case 'S': return '#28a745';
    case 'L': return '#ffc107';
    case 'M': return '#9c27b0';
    case 'Y': return '#007bff';
    case 'I': return '#17a2b8';
    case 'T': return '#28a745';
    case 'C': return '#ffc107';
    case 'K': return '#dc3545';
    case 'R': return '#17a2b8';
    case 'P': return '#6c757d';
    case 'H': return '#ff6b6b';
    default:  return '#6c757d';
  }
};

// ── VAT Report Result Section ───────────────────────────────────────────────────

const fmtNum = (v) => v != null ? new Intl.NumberFormat('he-IL').format(v) : '-';

const CorrectInfoTable = ({ title, info, docs, onDocClick, t, direction, sortCodeIdOptions = [], accountCardOptions = [], hideTitle = false, pcnReportDetails = null, incorrectInfo = null }) => {
  const [showDocs, setShowDocs] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  // Group ids expanded to reveal the individual grouped documents.
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const toggleGroup = (gid) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(gid)) next.delete(gid); else next.add(gid);
      return next;
    });
  };
  // Groups whose documents all share one VAT rate collapse into a single row (mirroring
  // what is reported to the tax authority); mixed-VAT groups stay as individual rows.
  const uniformVatGroupIds = useMemo(() => computeUniformVatGroupIds(pcnReportDetails), [pcnReportDetails]);
  const correctRows = info?.total_correct_inputs ?? info?.total_correct_outputs ?? [];
  const incorrectRows = incorrectInfo?.total_incorrect_inputs ?? incorrectInfo?.total_incorrect_outputs ?? [];
  const mergedByType = useMemo(() => {
    const map = {};
    [...correctRows, ...incorrectRows].forEach(row => {
      const type = row.type || '-';
      if (!map[type]) map[type] = { type, count: 0, vat: 0, sum: 0 };
      map[type].count += row.count ?? 0;
      map[type].vat += row.vat ?? 0;
      map[type].sum += row.sum ?? 0;
    });
    return Object.values(map).sort((a, b) => (a.type || '').localeCompare(b.type || ''));
  }, [correctRows, incorrectRows]);
  const totals = useMemo(() => {
    const count = (info?.count ?? 0) + (incorrectInfo?.count ?? 0);
    const vat = (info?.vat ?? 0) + (incorrectInfo?.vat ?? 0);
    const sum = (info?.sum ?? 0) + (incorrectInfo?.sum ?? 0);
    return { count, vat, sum };
  }, [info, incorrectInfo]);
  const searchLower = (searchQuery || '').trim().toLowerCase();
  const availableTypes = useMemo(() => {
    if (!docs?.length) return [];
    const types = [...new Set((docs || []).map(d => getDocDisplayType(d, pcnReportDetails)))].filter(x => x && x !== '-');
    return types.sort((a, b) => (a || '').localeCompare(b || ''));
  }, [docs, pcnReportDetails]);
  const filteredDocs = useMemo(() => {
    let list = docs || [];
    if (typeFilter) {
      list = list.filter(doc => getDocDisplayType(doc, pcnReportDetails) === typeFilter);
    }
    if (!searchLower) return list;
    return list.filter(doc => {
      const docAmt = getDocPcnAmounts(doc);
      const refRaw = String(doc.reference ?? '');
      const refDisplay = formatPcnReferenceDisplay(doc.reference);
      const caseId = String(doc.accountCaseId ?? doc.caseId ?? '');
      const dateStr = doc.documentDate ? new Date(doc.documentDate).toLocaleDateString('he-IL') : '';
      const vatStr = beautifyNumber(docAmt.vat, 2, true);
      const netStr = beautifyNumber(docAmt.net, 2, true);
      const grossStr = beautifyNumber(docAmt.gross, 2, true);
      const sortLabel = getSortCodeLabel(doc.sortCodeId, sortCodeIdOptions) ?? '';
      const debitLabel = getAccountLabel(doc.debitAccount, accountCardOptions) ?? '';
      const creditLabel = getAccountLabel(doc.creditAccount, accountCardOptions) ?? '';
      const typeLabel = getDocDisplayType(doc, pcnReportDetails);
      const searchable = `${refRaw} ${refDisplay} ${caseId} ${dateStr} ${vatStr} ${netStr} ${grossStr} ${sortLabel} ${debitLabel} ${creditLabel} ${typeLabel}`.toLowerCase();
      return searchable.includes(searchLower);
    });
  }, [docs, searchLower, typeFilter, sortCodeIdOptions, accountCardOptions, pcnReportDetails]);

  const hasRows = mergedByType.length > 0;
  if (!hasRows && !info) return null;
  const rows = hasRows ? mergedByType : correctRows;
  const displayTotals = hasRows ? totals : (info ? { count: info.count, vat: info.vat, sum: info.sum } : { count: 0, vat: 0, sum: 0 });
  const displayGrossTotal = (displayTotals.vat ?? 0) + (displayTotals.sum ?? 0);
  if (!hasRows && (!info || info.count === 0)) return null;
  const tableHeaderStyle = { padding: '12px 8px', textAlign: 'center', fontWeight: 600, borderBottom: '2px solid #dee2e6', whiteSpace: 'nowrap' };
  const tableCellStyle = { padding: '12px 8px', textAlign: 'center' };
  // Collapse uniform-VAT grouped documents into a single row (with _children for expand).
  const collapsedDocs = collapseGroupedSameVatDocs(filteredDocs, uniformVatGroupIds);
  const docGroups = (collapsedDocs || []).reduce((acc, doc) => {
    const type = getDocDisplayType(doc, pcnReportDetails);
    if (acc.length > 0 && acc[acc.length - 1].type === type) acc[acc.length - 1].docs.push(doc);
    else acc.push({ type, docs: [doc] });
    return acc;
  }, []);
  // Expand each type group into render rows: a collapsed parent, optionally followed by its
  // individual child rows when expanded. The type cell rowSpans across parents + children.
  const docRowsWithType = docGroups.flatMap((group) => {
    const rows = [];
    group.docs.forEach((doc) => {
      rows.push({ doc, isChild: false });
      if (doc._groupedCount > 1 && expandedGroups.has(doc._mergeKey || doc._groupId)) {
        (doc._children || []).forEach((child) => rows.push({ doc: child, isChild: true }));
      }
    });
    return rows.map((r, idx) => ({ ...r, type: group.type, isFirstInGroup: idx === 0, rowSpan: rows.length }));
  });
  return (
    <div style={{ marginTop: '10px', marginBottom: '24px' }}>
      {!hideTitle && (
      <div
        style={{ fontWeight: 600, fontSize: '14px', marginBottom: '10px', color: '#155724', textAlign: direction === 'ltr' ? 'left' : 'right', cursor: docs?.length ? 'pointer' : 'default', display: 'flex', alignItems: 'center', gap: '6px', justifyContent: direction === 'ltr' ? 'flex-start' : 'flex-end' }}
        onClick={() => docs?.length && setShowDocs(p => !p)}
      >
        {title}
        {docs?.length > 0 && <span style={{ fontSize: '12px', transform: showDocs ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>▼</span>}
      </div>
      )}
      {hideTitle && docs?.length > 0 && (
      <button
        type="button"
        style={{
          marginBottom: '10px',
          padding: '8px 14px',
          fontSize: '13px',
          fontWeight: 500,
          color: '#1565c0',
          backgroundColor: '#e3f2fd',
          border: '1px solid #90caf9',
          borderRadius: '8px',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          transition: 'background-color 0.2s, border-color 0.2s',
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        }}
        onClick={() => setShowDocs((p) => !p)}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#bbdefb';
          e.currentTarget.style.borderColor = '#64b5f6';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#e3f2fd';
          e.currentTarget.style.borderColor = '#90caf9';
        }}
      >
        <span>{showDocs ? t('HideDocumentList') : t('ShowDocumentList', { count: docs.length })}</span>
        <span style={{ display: 'inline-block', transform: showDocs ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', fontSize: '10px' }}>▼</span>
      </button>
      )}
      <div style={{ overflow: 'auto', border: '1px solid rgba(48, 79, 255, 0.4)', borderRadius: '6px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
          <thead style={{ background: 'linear-gradient(90deg,  rgb(48, 79, 255)', color: 'white' }}>
            <tr style={{ borderBottom: '2px solid rgba(255,255,255,0.3)' }}>
              {[t('Type') || 'Type', t('Count') || 'Count', t('VATAmount') || 'VAT', t('NetAmount'), t('AmountIncludesVAT')].map(h => (
                <th key={h} style={{ ...tableHeaderStyle, color: 'white', borderBottomColor: 'rgba(255,255,255,0.3)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={idx} style={{ borderBottom: '1px solid #e0e0e0', backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f5f5f5' }}>
                <td style={{ ...tableCellStyle, textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                    <Chip label={row.type} size="small"
                      sx={{ backgroundColor: invoiceTypeChipColor(row.type), color: 'white', fontWeight: 'bold', minWidth: 24 }} />
                    <span style={{ fontSize: '13px', color: '#37474f' }}>{t(PCNCodeNameKeys[row.type] || row.type)}</span>
                  </span>
                </td>
                <td style={tableCellStyle}>{row.count}</td>
                <td style={{ ...tableCellStyle, fontWeight: 600 }}>{fmtNum(row.vat)}</td>
                <td style={{ ...tableCellStyle, fontWeight: 600 }}>{fmtNum(row.sum)}</td>
                <td style={{ ...tableCellStyle, fontWeight: 600 }}>{fmtNum((row.vat ?? 0) + (row.sum ?? 0))}</td>
              </tr>
            ))}
            <tr style={{ background: 'linear-gradient(90deg, #e0e0e0', fontWeight: 700 , fontSize:'14px'}}>
              <td style={{ ...tableCellStyle }}>{t('Total') || 'Total'}</td>
              <td style={{ ...tableCellStyle }}>{displayTotals.count}</td>
              <td style={{ ...tableCellStyle }}>{fmtNum(displayTotals.vat)}</td>
              <td style={{ ...tableCellStyle }}>{fmtNum(displayTotals.sum)}</td>
              <td style={{ ...tableCellStyle }}>{fmtNum(displayGrossTotal)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      {showDocs && docs?.length > 0 && (
        <div style={{ marginTop: '10px' }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: '#1565c0', marginBottom: '6px', textAlign: direction === 'rtl' ? 'right' : 'left' }}>{t('Details')}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center', marginBottom: '8px', flexDirection: direction === 'rtl' ? 'row-reverse' : 'row' }}>
            <TextField
              size="small"
              placeholder={t('Search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                ...(direction === 'rtl'
                  ? { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }
                  : { endAdornment: <InputAdornment position="end"><SearchIcon fontSize="small" /></InputAdornment> }
                ),
              }}
              sx={{ minWidth: 200, flex: 1, maxWidth: 320, '& .MuiInputBase-input': { textAlign: direction === 'rtl' ? 'right' : 'left' } }}
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="details-type-filter-label">{t('Type')}</InputLabel>
              <Select
                labelId="details-type-filter-label"
                value={typeFilter}
                label={t('Type')}
                onChange={(e) => setTypeFilter(e.target.value || '')}
              >
                <MenuItem value="">{t('All') || 'All'}</MenuItem>
                {availableTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}{t(PCNCodeNameKeys[type] || type) ? ` — ${t(PCNCodeNameKeys[type] || type)}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div style={{ overflow: 'auto', border: '1px solid #90caf9', borderRadius: '6px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead style={{ backgroundColor: '#e3f2fd' }}>
              <tr style={{ borderBottom: '2px solid rgb(37, 78, 118)' }}>
                {[t('Type') || 'סוג', t('CompanyId') || 'VAT Number', t('SortCode') || 'Sort Code', t('DebitAccount') || 'Debit Account', t('CreditAccount') || 'Credit Account', t('Reference') || 'Reference', t('DocumentDate') || 'Date', t('VATAmount') || 'VAT', t('NetAmount'), t('AmountIncludesVAT')].map(h => (
                  <th key={h} style={tableHeaderStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {docRowsWithType.length === 0 ? (
                <tr>
                  <td colSpan={10} style={{ ...tableCellStyle, padding: '24px', color: '#6c757d', textAlign: 'center' }}>
                    {searchLower ? (t('NoMatchingDocuments') || 'No matching documents') : (t('NoDocuments') || 'No documents')}
                  </td>
                </tr>
              ) : docRowsWithType.map(({ doc, type, isFirstInGroup, rowSpan, isChild }, idx) => {
                const docAmt = getDocPcnAmounts(doc);
                const docId = doc.id ?? doc.Id;
                const isGroupedParent = !isChild && doc._groupedCount > 1;
                const baseBg = isChild ? '#faf5ff' : (idx % 2 === 0 ? '#ffffff' : '#e3f2fd');
                return (
                  <tr key={`${isChild ? 'child' : 'row'}-${doc._groupId ?? ''}-${docId ?? idx}`}
                    style={{ borderBottom: isChild ? '1px dashed #e1d5f5' : '1px solid #bbdefb', backgroundColor: baseBg, cursor: onDocClick ? 'pointer' : 'default', transition: 'background-color 0.2s ease' }}
                    onClick={() => {
                      if (docId && doc.documentType && onDocClick) {
                        onDocClick({ id: docId, documentType: doc.documentType });
                      }
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isChild ? '#f3e9ff' : '#bbdefb'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = baseBg; }}
                  >
                    {isFirstInGroup && (
                      <td style={{ ...tableCellStyle, verticalAlign: 'middle', backgroundColor: `${invoiceTypeChipColor(type)}22`, textAlign: direction === 'rtl' ? 'right' : 'left' }} rowSpan={rowSpan}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                          <Chip label={type} size="small" sx={{ backgroundColor: invoiceTypeChipColor(type), color: 'white', fontWeight: 'bold', minWidth: 24 }} />
                          <span style={{ fontSize: '12px', color: '#37474f' }}>{t(PCNCodeNameKeys[type] || type)}</span>
                        </span>
                      </td>
                    )}
                    <td style={tableCellStyle}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', justifyContent: 'center' }}>
                        {isChild && <span style={{ color: '#5e35b1' }}>↳</span>}
                        {doc.accountCaseId || '-'}
                        {isGroupedParent && (
                          <Chip
                            size="small"
                            onClick={(e) => { e.stopPropagation(); toggleGroup(doc._mergeKey || doc._groupId); }}
                            label={`${t('Grouped', { defaultValue: 'Grouped' })} (${doc._groupedCount}) ${expandedGroups.has(doc._mergeKey || doc._groupId) ? '▲' : '▼'}`}
                            title={(doc._groupedUploads || []).join(', ')}
                            sx={{ height: 18, fontSize: 10, backgroundColor: '#ede7f6', color: '#5e35b1', cursor: 'pointer', '&:hover': { backgroundColor: '#d1c4e9' } }}
                          />
                        )}
                      </span>
                    </td>
                    <td style={tableCellStyle}>{getSortCodeLabel(doc.sortCodeId, sortCodeIdOptions) ?? '-'}</td>
                    <td style={tableCellStyle}>{getAccountLabel(doc.debitAccount, accountCardOptions) ?? '-'}</td>
                    <td style={tableCellStyle}>{getAccountLabel(doc.creditAccount, accountCardOptions) ?? '-'}</td>
                    <td style={tableCellStyle}>{formatPcnReferenceDisplay(doc.reference)}</td>
                    <td style={tableCellStyle}>{doc.documentDate ? new Date(doc.documentDate).toLocaleDateString('he-IL') : '-'}</td>
                    <td style={{ ...tableCellStyle, fontWeight: 600 }}>{beautifyNumber(docAmt.vat, 2, true)}</td>
                    <td style={{ ...tableCellStyle, fontWeight: 600 }}>{beautifyNumber(docAmt.net, 2, true)}</td>
                    <td style={{ ...tableCellStyle, fontWeight: 600 }}>{beautifyNumber(docAmt.gross, 2, true)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </div>
      )}
    </div>
  );
};

const VatReportErrorsSection = ({ vatReportResult, pcnReportDetails, expanded, onToggle, onInvoiceClick, t, direction, reportStartDate, reportEndDate, sortCodeIdOptions = [], accountCardOptions = [] }) => {
  const ri = vatReportResult?.report_information ?? vatReportResult?.reportInformation;
  const [errorsSearchQuery, setErrorsSearchQuery] = useState('');
  const [errorsSectionFilter, setErrorsSectionFilter] = useState('all'); // 'all' | 'input' | 'output'

  const incorrectInputs = ri?.inputs_information?.incorrect_inputs_information?.incorrect_inputs ?? ri?.inputsInformation?.incorrectInputsInformation?.incorrectInputs ?? [];
  const incorrectOutputs = ri?.outputs_information?.incorrect_outputs_information?.incorrect_outputs ?? ri?.outputsInformation?.incorrectOutputsInformation?.incorrectOutputs ?? [];
  const allIncorrect = [
    ...incorrectInputs.map(inv => ({ ...inv, _section: 'input' })),
    ...incorrectOutputs.map(inv => ({ ...inv, _section: 'output' }))
  ];
  const errorsSearchLower = (errorsSearchQuery || '').trim().toLowerCase();
  const filteredIncorrect = useMemo(() => {
    let list = allIncorrect;
    if (errorsSectionFilter !== 'all') {
      list = list.filter(inv => inv._section === errorsSectionFilter);
    }
    if (!errorsSearchLower) return list;
    return list.filter(inv => {
      const invAmt = getInvPcnAmounts(inv);
      const ref = String(inv.invoice_reference_number ?? '');
      const refDisplay = formatPcnReferenceDisplay(inv.invoice_reference_number);
      const vatNum = String(inv.invoice_customer_vat_number ?? '');
      const dateStr = String(inv.invoice_date ?? '');
      const typeStr = String(inv.invoice_type ?? '');
      const codeStr = String(inv.invoice_error_code ?? '');
      // Search the text as displayed, so a Hebrew search term matches a translated message.
      const msgStr = translateShaamMessage(inv.invoice_error_message, t);
      const vatStr = beautifyNumber(inv.invoice_vat, 2, true);
      const netStr = beautifyNumber(invAmt.net, 2, true);
      const grossStr = beautifyNumber(invAmt.gross, 2, true);
      const searchable = `${ref} ${refDisplay} ${vatNum} ${dateStr} ${typeStr} ${codeStr} ${msgStr} ${vatStr} ${netStr} ${grossStr}`.toLowerCase();
      return searchable.includes(errorsSearchLower);
    });
  }, [allIncorrect, errorsSectionFilter, errorsSearchLower]);

  if (!ri) return null;

  const allDocs = [
    ...(pcnReportDetails?.incomes ?? []).map(d => ({ ...d, documentType: d.documentType || 'Incomes' })),
    ...(pcnReportDetails?.expenses ?? []).map(d => ({ ...d, documentType: d.documentType || 'Expenses' })),
    ...(pcnReportDetails?.equipmentTransactions ?? []).map(d => ({ ...d, documentType: d.documentType || 'EquipmentTransactions' })),
  ];

  
  const pctInputs = ri?.inputs_information?.percent_correct_inputs ?? ri?.inputsInformation?.percentCorrectInputs;
  const pctOutputs = ri?.outputs_information?.percent_correct_outputs ?? ri?.outputsInformation?.percentCorrectOutputs;
  const correctInputsInfo = ri?.inputs_information?.correct_inputs_information ?? ri?.inputsInformation?.correctInputsInformation;
  const correctOutputsInfo = ri?.outputs_information?.correct_outputs_information ?? ri?.outputsInformation?.correctOutputsInformation;  
  const isAllCorrect = allIncorrect.length === 0 && !ri.error_code;
  const headerBg = isAllCorrect ? '#d4edda' : '#f8d7da';
  const headerBorder = isAllCorrect ? '#c3e6cb' : '#f5c6cb';
  const headerColor = isAllCorrect ? '#155724' : '#721c24';
  const actionType = vatReportResult?._action;
  const successKey = actionType === 2
    ? 'SendVatDetailedReportCheckSuccess'
    : actionType === 3
      ? 'SendVatDetailedReportCheckAndReportSuccess'
      : 'SendVatDetailedReportSuccess';
  const headerMessage = isAllCorrect
    ? t(successKey)
    : (translateShaamMessage(ri.error_message || vatReportResult?.message, t)
      || t('SendVatDetailedReportIncorrect'));

  const errorsVatSum = allIncorrect.reduce((s, inv) => s + (inv.invoice_vat ?? 0), 0);
  const errorsNetSum = allIncorrect.reduce((s, inv) => s + (inv.invoice_sum ?? 0), 0);
  const errorsGrossSum = allIncorrect.reduce((s, inv) => s + getInvPcnAmounts(inv).gross, 0);

  const incorrectOutputsInfo = ri?.outputs_information?.incorrect_outputs_information ?? ri?.outputsInformation?.incorrectOutputsInformation;
  const incorrectInputsInfo = ri?.inputs_information?.incorrect_inputs_information ?? ri?.inputsInformation?.incorrectInputsInformation;
  const hasIncorrectOutputs = incorrectOutputsInfo && (incorrectOutputsInfo.incorrect_outputs?.length ?? incorrectOutputsInfo.incorrectOutputs?.length ?? 0) > 0;
  const hasIncorrectInputs = incorrectInputsInfo && (incorrectInputsInfo.incorrect_inputs?.length ?? incorrectInputsInfo.incorrectInputs?.length ?? 0) > 0;

  const totalOutputsVat = (correctOutputsInfo?.vat ?? 0) + (incorrectOutputsInfo?.vat ?? 0);
  const totalOutputsSum = (correctOutputsInfo?.sum ?? 0) + (incorrectOutputsInfo?.sum ?? 0);
  const totalInputsVat = (correctInputsInfo?.vat ?? 0) + (incorrectInputsInfo?.vat ?? 0);
  const totalInputsSum = (correctInputsInfo?.sum ?? 0) + (incorrectInputsInfo?.sum ?? 0);
  const hasIncomes = totalOutputsSum > 0 || totalOutputsVat > 0 || (correctOutputsInfo?.count ?? 0) + (incorrectOutputsInfo?.count ?? 0) > 0;
  const hasExpenses = totalInputsSum > 0 || totalInputsVat > 0 || (correctInputsInfo?.count ?? 0) + (incorrectInputsInfo?.count ?? 0) > 0;
  const netVat = totalOutputsVat - totalInputsVat;
  const isRefund = netVat < 0;

  // Prefer report_summary from API (same as PDF/TXT simulator); fallback to computed from report_information
  const summary = vatReportResult?.report_summary ?? vatReportResult?.reportSummary;
  const exemptTransactions = summary?.exempt_transactions ?? summary?.exemptTransactions ?? 0;
  const taxableTransactions = summary?.taxable_transactions ?? summary?.taxableTransactions ?? totalOutputsSum;
  const transactionVat = summary?.transaction_vat ?? summary?.transactionVat ?? totalOutputsVat;
  const otherInputVat = summary?.other_input_vat ?? summary?.otherInputVat ?? totalInputsVat;
  const equipmentInputVat = summary?.equipment_input_vat ?? summary?.equipmentInputVat ?? 0;
  const amountToPayOrRefund = summary?.amount_to_pay_or_refund ?? summary?.amountToPayOrRefund ?? netVat;

  // Reporting period label (e.g. "02/2026") — always use endDate as the reported month
  const reportPeriodLabel = (() => {
    const end = reportEndDate?.toDate?.() ?? reportEndDate;
    const d = end || (reportStartDate?.toDate?.() ?? reportStartDate);
    if (!d) return '';
    const date = d instanceof Date ? d : new Date(d);
    if (isNaN(date.getTime())) return '';
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const y = date.getFullYear();
    return `${m}/${y}`;
  })();

  const errorMessage = ((ri.error_code != null && ri.error_code !== 0) || (ri.error_message && ri.error_message.trim()) || (allIncorrect.length > 0 && vatReportResult?.message))
    ? `${translateShaamMessage(ri.error_message || vatReportResult?.message, t)}${ri.error_code != null && ri.error_code !== 0 ? ` (${ri.error_code})` : ''}`.trim()
    : null;
  const successMessage = isAllCorrect && vatReportResult?.message ? vatReportResult.message : null;
  const messageCellText = successMessage
    ? translateShaamMessage(successMessage, t)
    : (errorMessage || '—');

  // Inline styles matching simulator row/explainText/explainTextB/errfont + success
  const row2Style = { backgroundColor: '#f0f2f5', height: 32 };
  const row1Style = { backgroundColor: '#fff', height: 32 };
  const explainTextStyle = { color: '#3c4043', fontSize: '13px' };
  const explainTextBStyle = { fontWeight: 600, color: '#1a73e8' };
  const errfontStyle = { color: '#c5221f', fontSize: '12px', fontWeight: 500 };
  const successMessageStyle = { color: '#155724', fontSize: '12px', fontWeight: 600 };

  return (
    <div style={{ marginBottom: '15px' }}>
      <div
        style={{
          display: 'flex', alignItems: 'center', padding: '12px 16px',
          backgroundColor: headerBg, border: `1px solid ${headerBorder}`,
          borderRadius: '8px', cursor: 'pointer', transition: 'background-color 0.2s ease',
        }}
        onClick={onToggle}
      >
        <div style={{ flex: 1, textAlign: direction === 'ltr' ? 'left' : 'right', fontWeight: 500, color: headerColor }}>
          {headerMessage}
          {ri.error_code ? ` (${ri.error_code})` : ''}
          {allIncorrect.length > 0 && ` — ${allIncorrect.length} ${t('IncorrectInvoices') || 'incorrect invoices'}`}
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {pctInputs != null && (
            <Chip label={`${t('Inputs') || 'Inputs'}: ${pctInputs}%`} size="small"
              sx={{ backgroundColor: pctInputs === 100 ? '#c8e6c9' : '#ffcdd2', fontWeight: 600, fontSize: 11 }} />
          )}
          {pctOutputs != null && (
            <Chip label={`${t('Outputs') || 'Outputs'}: ${pctOutputs}%`} size="small"
              sx={{ backgroundColor: pctOutputs === 100 ? '#c8e6c9' : '#ffcdd2', fontWeight: 600, fontSize: 11 }} />
          )}
          <span style={{
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease', fontSize: '14px', color: headerColor,
          }}>▼</span>
        </div>
      </div>

      {expanded && (
        <div style={{ marginTop: '10px', border: '1px solid #dee2e6', borderRadius: '8px', overflow: 'hidden', padding: '16px 20px' }} dir={direction}>
          {/* Top summary - errors widget left, table right */}
          <div style={{
            marginBottom: 20,
            padding: '12px 16px',
            backgroundColor: '#E8EAED',
            borderRadius: 8,
            border: '1px solid #dadce0',
          }}>
            <h3 style={{
              margin: '0 0 10px 0',
              fontSize: 14,
              fontWeight: 600,
              color: '#5f6368',
              textAlign: direction === 'rtl' ? 'right' : 'left',
            }}>
              {t('Summary')}
            </h3>
            <div style={{
              display: 'flex',
              flexDirection: direction === 'rtl' ? 'row-reverse' : 'row',
              alignItems: 'flex-start',
              gap: 16,
              width: '100%',
            }}>
              {/* Errors widget - small, left of table */}
              {allIncorrect.length > 0 && (
                <div style={{
                  flexShrink: 0,
                  width: 180,
                  padding: '8px 10px',
                  backgroundColor: '#F8F2E4',
                  borderRadius: 8,
                  border: '1px solid #eadfc9',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                }}>
                  <div style={{
                    textAlign: 'center',
                    marginBottom: 8,
                    padding: '4px 8px',
                    backgroundColor: '#D4EEF9',
                    borderRadius: 4,
                    fontSize: 11,
                    fontWeight: 600,
                    color: '#1a237e',
                  }}>
                    {t('Errors') || 'Errors'}
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                    <tbody>
                      <tr>
                        <td style={{ padding: '3px 6px 3px 0', color: '#37474f', textAlign: direction === 'rtl' ? 'right' : 'left' }}>{t('Count') || 'Count'}</td>
                        <td style={{ padding: '3px 0', fontWeight: 600, color: '#1a1a1a', textAlign: direction === 'rtl' ? 'left' : 'right' }}>{allIncorrect.length}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '3px 6px 3px 0', color: '#37474f', textAlign: direction === 'rtl' ? 'right' : 'left' }}>{t('VATAmount')}</td>
                        <td style={{ padding: '3px 0', fontWeight: 600, color: '#1a1a1a', textAlign: direction === 'rtl' ? 'left' : 'right' }}>{beautifyNumber(errorsVatSum, 2)} ש&quot;ח</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '3px 6px 3px 0', color: '#37474f', textAlign: direction === 'rtl' ? 'right' : 'left' }}>{t('NetAmount')}</td>
                        <td style={{ padding: '3px 0', fontWeight: 600, color: '#1a1a1a', textAlign: direction === 'rtl' ? 'left' : 'right' }}>{beautifyNumber(errorsNetSum, 2)} ש&quot;ח</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '3px 6px 3px 0', color: '#37474f', textAlign: direction === 'rtl' ? 'right' : 'left' }}>{t('AmountIncludesVAT')}</td>
                        <td style={{ padding: '3px 0', fontWeight: 600, color: '#1a1a1a', textAlign: direction === 'rtl' ? 'left' : 'right' }}>{beautifyNumber(errorsGrossSum, 2)} ש&quot;ח</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              <div style={{ flex: 1, overflow: 'auto', minWidth: 0 }}>
                <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: 13 }}>
                  <tbody>
                    <tr style={row2Style}>
                      <td style={{ width: '11%', padding: '6px 8px', textAlign: direction === 'rtl' ? 'right' : 'left', ...explainTextStyle }}>{t('ReportingPeriod')}</td>
                      <td style={{ width: '12%', padding: '6px 8px', textAlign: direction === 'rtl' ? 'left' : 'right', ...explainTextBStyle }}>{reportPeriodLabel || '—'}</td>
                      <td style={{ width: '11%', padding: '6px 8px', textAlign: direction === 'rtl' ? 'right' : 'left', ...explainTextStyle }}>{t('ReportSource')}</td>
                      <td style={{ width: '12%', padding: '6px 8px', textAlign: direction === 'rtl' ? 'left' : 'right', ...explainTextBStyle }}>—</td>
                      <td style={{ width: '11%', padding: '6px 8px', textAlign: direction === 'rtl' ? 'right' : 'left', ...explainTextStyle }}>{t('ReportDate')}</td>
                      <td style={{ width: '12%', padding: '6px 8px', textAlign: direction === 'rtl' ? 'left' : 'right', ...explainTextBStyle }}>—</td>
                      <td style={{ width: '9%', padding: '6px 8px', textAlign: direction === 'rtl' ? 'right' : 'left', ...explainTextStyle }}>{t('Errors')}</td>
                      <td style={{ width: '22%', padding: '6px 8px', textAlign: direction === 'rtl' ? 'left' : 'right', ...(successMessage ? successMessageStyle : errorMessage ? errfontStyle : explainTextBStyle) }}>{messageCellText}</td>
                    </tr>
                    <tr style={row1Style}>
                      <td style={{ padding: '6px 8px', textAlign: direction === 'rtl' ? 'right' : 'left', ...explainTextStyle }}>{t('TotalTaxableTransactions')}</td>
                      <td style={{ padding: '6px 8px', textAlign: direction === 'rtl' ? 'left' : 'right', ...explainTextBStyle }}>{beautifyNumber(Math.round(taxableTransactions))}&nbsp;<span style={explainTextStyle}>ש&quot;ח</span></td>
                      <td style={{ padding: '6px 8px', textAlign: direction === 'rtl' ? 'right' : 'left', ...explainTextStyle }}>{t('VATOnTaxableTransactions')}</td>
                      <td style={{ padding: '6px 8px', textAlign: direction === 'rtl' ? 'left' : 'right', ...explainTextBStyle }}>{beautifyNumber(Math.round(transactionVat))}&nbsp;<span style={explainTextStyle}>ש&quot;ח</span></td>
                      <td style={{ padding: '6px 8px', textAlign: direction === 'rtl' ? 'right' : 'left', ...explainTextStyle }}>{t('ExemptZeroTransactions')}</td>
                      <td style={{ padding: '6px 8px', textAlign: direction === 'rtl' ? 'left' : 'right', ...explainTextBStyle }}>{beautifyNumber(Math.round(exemptTransactions))}&nbsp;<span style={explainTextStyle}>ש&quot;ח</span></td>
                      <td colSpan={2} style={{ padding: '6px 8px' }} />
                    </tr>
                    <tr style={row2Style}>
                      <td style={{ padding: '6px 8px', textAlign: direction === 'rtl' ? 'right' : 'left', ...explainTextStyle }}>{t('EquipmentInputs')}</td>
                      <td style={{ padding: '6px 8px', textAlign: direction === 'rtl' ? 'left' : 'right', ...explainTextBStyle }}>{beautifyNumber(Math.round(equipmentInputVat))}&nbsp;<span style={explainTextStyle}>ש&quot;ח</span></td>
                      <td style={{ padding: '6px 8px', textAlign: direction === 'rtl' ? 'right' : 'left', ...explainTextStyle }}>{t('OtherInputs')}</td>
                      <td style={{ padding: '6px 8px', textAlign: direction === 'rtl' ? 'left' : 'right', ...explainTextBStyle }}>{beautifyNumber(Math.round(otherInputVat))}&nbsp;<span style={explainTextStyle}>ש&quot;ח</span></td>
                      <td style={{ padding: '6px 8px', textAlign: direction === 'rtl' ? 'right' : 'left', ...explainTextStyle }}>{amountToPayOrRefund > 0 ? t('VATToPay') : t('AmountForRefundTransmitted')}</td>
                      <td style={{ padding: '6px 8px', textAlign: direction === 'rtl' ? 'left' : 'right', ...explainTextBStyle, ...(amountToPayOrRefund < 0 ? { color: '#d32f2f' } : {}) }}>{amountToPayOrRefund < 0 ? `(${beautifyNumber(Math.abs(Math.round(amountToPayOrRefund)))})` : beautifyNumber(Math.round(amountToPayOrRefund))}&nbsp;<span style={explainTextStyle}>ש&quot;ח</span></td>
                      <td colSpan={2} style={{ padding: '6px 8px' }} />
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Section A: Incomes */}
          {(correctOutputsInfo?.count ?? 0) + (incorrectOutputsInfo?.count ?? 0) > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{
                margin: '0 0 6px 0',
                fontSize: '16px',
                fontWeight: 600,
                color: '#2e7d32',
                borderBottom: '2px solid #28a745',
                paddingBottom: '8px',
                textAlign: direction === 'rtl' ? 'right' : 'left',
              }}>
                {t('Incomes')}
              </h3>
              {hasIncorrectOutputs && (
                <p style={{
                  margin: '0 0 12px 0',
                  padding: '10px 14px',
                  fontSize: '15px',
                  fontWeight: 700,
                  color: '#1a237e',
                  backgroundColor: '#e8eaf6',
                  borderRadius: '8px',
                  border: '1px solid #9fa8da',
                  textAlign: direction === 'rtl' ? 'right' : 'left',
                  lineHeight: 1.5,
                }}>
                  {t('CorrectOutputs') || 'Correct'}: <strong>{correctOutputsInfo.count}</strong> · {t('WithErrors') || 'With errors'}: <strong>{incorrectOutputsInfo.count}</strong> · {t('Total') || 'Total'}: <strong>{(correctOutputsInfo.count || 0) + (incorrectOutputsInfo?.count ?? 0)}</strong> 
                </p>
              )}
              <CorrectInfoTable
                title=""
                info={correctOutputsInfo}
                incorrectInfo={incorrectOutputsInfo}
                docs={(pcnReportDetails?.incomes ?? []).map(d => ({ ...d, documentType: d.documentType || 'Incomes' }))}
                onDocClick={onInvoiceClick}
                t={t}
                direction={direction}
                sortCodeIdOptions={sortCodeIdOptions}
                accountCardOptions={accountCardOptions}
                hideTitle
                pcnReportDetails={pcnReportDetails}
              />
            </div>
          )}

          {/* Section B: Expenses */}
          {(correctInputsInfo?.count ?? 0) + (incorrectInputsInfo?.count ?? 0) > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{
                margin: '0 0 6px 0',
                fontSize: '16px',
                fontWeight: 600,
                color: '#c62828',
                borderBottom: '2px solid #dc3545',
                paddingBottom: '8px',
                textAlign: direction === 'rtl' ? 'right' : 'left',
              }}>
                {t('Expenses')}
              </h3>
              {hasIncorrectInputs && (
                <p style={{
                  margin: '0 0 12px 0',
                  padding: '10px 14px',
                  fontSize: '15px',
                  fontWeight: 700,
                  color: '#1a237e',
                  backgroundColor: '#e8eaf6',
                  borderRadius: '8px',
                  border: '1px solid #9fa8da',
                  textAlign: direction === 'rtl' ? 'right' : 'left',
                  lineHeight: 1.5,
                }}>
                  {t('CorrectInputs') || 'Correct'}: <strong>{correctInputsInfo.count}</strong> · {t('WithErrors') || 'With errors'}: <strong>{incorrectInputsInfo.count}</strong> · {t('Total') || 'Total'}: <strong>{(correctInputsInfo.count || 0) + (incorrectInputsInfo?.count ?? 0)}</strong>
                </p>
              )}
              <CorrectInfoTable
                title=""
                info={correctInputsInfo}
                incorrectInfo={incorrectInputsInfo}
                docs={[
                  ...(pcnReportDetails?.expenses ?? []).map(d => ({ ...d, documentType: d.documentType || 'Expenses' })),
                  ...(pcnReportDetails?.equipmentTransactions ?? []).map(d => ({ ...d, documentType: d.documentType || 'EquipmentTransactions' })),
                ]}
                onDocClick={onInvoiceClick}
                t={t}
                direction={direction}
                sortCodeIdOptions={sortCodeIdOptions}
                accountCardOptions={accountCardOptions}
                hideTitle
                pcnReportDetails={pcnReportDetails}
              />
            </div>
          )}

          {/* Section C: Errors */}
          {allIncorrect.length > 0 && (
            <div style={{
              marginBottom: '16px',
              padding: '16px',
              backgroundColor: '#fffaf5',
              borderRadius: '10px',
              border: '1px solid #ffe0b2',
            }}>
              <h3 style={{
                textAlign: direction === 'rtl' ? 'right' : 'left',
                marginBottom: '15px',
                color: '#e65100',
                fontSize: '18px',
                fontWeight: 600,
                borderBottom: '2px solid #007bff',
                paddingBottom: '8px',
              }}>
                {t('IncorrectInvoices') || 'Incorrect Invoices'} ({filteredIncorrect.length}{filteredIncorrect.length !== allIncorrect.length ? ` / ${allIncorrect.length}` : ''})
              </h3>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <TextField
                  size="small"
                  placeholder={t('Search')}
                  value={errorsSearchQuery}
                  onChange={(e) => setErrorsSearchQuery(e.target.value)}
                  InputProps={{
                    ...(direction === 'rtl'
                      ? { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }
                      : { endAdornment: <InputAdornment position="end"><SearchIcon fontSize="small" /></InputAdornment> }
                    ),
                  }}
                  sx={{ minWidth: 200, '& .MuiInputBase-input': { textAlign: direction === 'rtl' ? 'right' : 'left' } }}
                />
                <Box component="span" sx={{ fontSize: 13, fontWeight: 600, color: '#5f6368', mx: 0.5 }}>{t('Filter') || 'Filter'}:</Box>
                <Chip
                  label={t('All') || 'All'}
                  onClick={() => setErrorsSectionFilter('all')}
                  color={errorsSectionFilter === 'all' ? 'primary' : 'default'}
                  variant={errorsSectionFilter === 'all' ? 'filled' : 'outlined'}
                  size="small"
                  sx={{ cursor: 'pointer' }}
                />
                <Chip
                  label={t('Incomes')}
                  onClick={() => setErrorsSectionFilter('output')}
                  color={errorsSectionFilter === 'output' ? 'primary' : 'default'}
                  variant={errorsSectionFilter === 'output' ? 'filled' : 'outlined'}
                  size="small"
                  sx={{ cursor: 'pointer' }}
                />
                <Chip
                  label={t('Expenses')}
                  onClick={() => setErrorsSectionFilter('input')}
                  color={errorsSectionFilter === 'input' ? 'primary' : 'default'}
                  variant={errorsSectionFilter === 'input' ? 'filled' : 'outlined'}
                  size="small"
                  sx={{ cursor: 'pointer' }}
                />
              </Box>
              <div style={{ overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead style={{ backgroundColor: '#e3f2fd' }}>
                    <tr style={{ borderBottom: '2px solid rgb(37, 78, 118)' }}>
                      {['InvoiceType', 'CompanyId', 'InvoiceDate', 'Reference', 'VATAmount', 'NetAmount', 'AmountIncludesVAT', 'ErrorCode', 'ErrorMessage'].map(key => (
                        <th key={key} style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 600, borderBottom: '2px solid #dee2e6', whiteSpace: 'nowrap' }}>
                          {t(key) || key}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredIncorrect.length === 0 ? (
                      <tr>
                        <td colSpan={9} style={{ padding: '24px', textAlign: 'center', color: '#6c757d' }}>
                          {(errorsSearchQuery || errorsSectionFilter !== 'all') ? (t('NoMatchingInvoices') || 'No matching invoices') : (t('NoIncorrectInvoices') || 'No incorrect invoices')}
                        </td>
                      </tr>
                    ) : filteredIncorrect.map((inv, idx) => {
                      const invAmounts = getInvPcnAmounts(inv);
                      return (
                      <tr key={idx}
                        style={{
                          borderBottom: '1px solid #dee2e6',
                          backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f8f9fa',
                          cursor: onInvoiceClick ? 'pointer' : 'default',
                          transition: 'background-color 0.2s ease',
                        }}
                        onClick={() => {
                          const matchedDoc = findMatchingPcnDoc(inv, allDocs, pcnReportDetails);
                          const docId = matchedDoc?.id ?? matchedDoc?.Id;
                          if (docId && matchedDoc?.documentType && onInvoiceClick) {
                            onInvoiceClick({ id: docId, documentType: matchedDoc.documentType });
                          }
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e3f2fd'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = idx % 2 === 0 ? '#ffffff' : '#f8f9fa'; }}
                      >
                        <td style={{ padding: '12px 8px', textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                          {inv.invoice_type ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                              <Chip label={inv.invoice_type} size="small"
                                sx={{ backgroundColor: invoiceTypeChipColor(inv.invoice_type), color: 'white', fontWeight: 'bold', minWidth: 24 }} />
                              <span style={{ fontSize: '13px', color: '#37474f' }}>{t(PCNCodeNameKeys[inv.invoice_type] || inv.invoice_type)}</span>
                            </span>
                          ) : '-'}
                        </td>
                        <td style={{ padding: '12px 8px', textAlign: 'center' }}>{inv.invoice_customer_vat_number ?? '-'}</td>
                        <td style={{ padding: '12px 8px', textAlign: 'center' }}>{inv.invoice_date || '-'}</td>
                        <td style={{ padding: '12px 8px', textAlign: 'center' }}>{formatPcnReferenceDisplay(inv.invoice_reference_number)}</td>
                        <td style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 600 }}>{beautifyNumber(inv.invoice_vat, 2, true)}</td>
                        <td style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 600 }}>{beautifyNumber(invAmounts.net, 2, true)}</td>
                        <td style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 600 }}>{beautifyNumber(invAmounts.gross, 2, true)}</td>
                        <td style={{ padding: '12px 8px', textAlign: 'center', color: '#d32f2f', fontWeight: 600 }}>{inv.invoice_error_code ?? '-'}</td>
                        <td style={{ padding: '12px 8px', textAlign: direction === 'rtl' ? 'right' : 'left', color: '#d32f2f', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {translateShaamMessage(inv.invoice_error_message, t) || '-'}
                        </td>
                      </tr>
                    );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Incorrect Invoice Detail Modal ────────────────────────────────────────────

const IncorrectInvoiceDetailModal = ({ open, onClose, invoice, t, direction }) => {
  if (!invoice) return null;

  const rows = [
    { label: t('InvoiceType') || 'Invoice Type', value: invoice.invoice_type },
    { label: t('VatNumber') || 'VAT Number', value: invoice.invoice_customer_vat_number },
    { label: t('InvoiceDate') || 'Invoice Date', value: invoice.invoice_date },
    { label: t('InvoiceReferenceGroup') || 'Reference Group', value: invoice.invoice_reference_group },
    { label: t('Reference') || 'Reference', value: formatPcnReferenceDisplay(invoice.invoice_reference_number) },
    { label: t('VATAmount') , value: invoice.invoice_vat != null ? new Intl.NumberFormat('he-IL').format(invoice.invoice_vat) : '-' },
    { label: t('InvoiceSign') || 'Sign', value: invoice.invoice_sign },
    { label: t('NetAmount'), value: invoice.invoice_sum != null ? new Intl.NumberFormat('he-IL').format(invoice.invoice_sum) : '-' },
    { label: t('AmountIncludesVAT'), value: invoice.invoice_sum != null || invoice.invoice_vat != null ? new Intl.NumberFormat('he-IL').format(getInvPcnAmounts(invoice).gross) : '-' },
    { label: t('ConfirmationNumber') || 'Confirmation Number', value: invoice.invoice_confirmation_number },
    { label: t('ErrorCode') || 'Error Code', value: invoice.invoice_error_code, isError: true },
    { label: t('ErrorMessage') || 'Error Message', value: translateShaamMessage(invoice.invoice_error_message, t), isError: true },
  ];

  return (
    <Modal open={open} onClose={onClose} sx={{ zIndex: 1400 }} disableEnforceFocus disableAutoFocus>
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '90%',
        maxWidth: 600,
        maxHeight: '90vh',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: 24,
        padding: 3,
        outline: 'none',
        overflow: 'auto',
      }} className={`modal-container ${direction}`}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid #dee2e6', paddingBottom: '15px' }}>
          <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 600, color: 'rgba(0, 0, 0, 0.90)' }}>
            {t('IncorrectInvoiceDetails') || 'Incorrect Invoice Details'}
          </h2>
          <div style={{ cursor: 'pointer', padding: '4px' }} onClick={onClose} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && onClose()}>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M15.2501 4.75794C14.9251 4.43294 14.4001 4.43294 14.0751 4.75794L10.0001 8.82461L5.9251 4.74961C5.6001 4.42461 5.0751 4.42461 4.7501 4.74961C4.4251 5.07461 4.4251 5.59961 4.7501 5.92461L8.8251 9.99961L4.7501 14.0746C4.4251 14.3996 4.4251 14.9246 4.7501 15.2496C5.0751 15.5746 5.6001 15.5746 5.9251 15.2496L10.0001 11.1746L14.0751 15.2496C14.4001 15.5746 14.9251 15.5746 15.2501 15.2496C15.5751 14.9246 15.5751 14.3996 15.2501 14.0746L11.1751 9.99961L15.2501 5.92461C15.5668 5.60794 15.5668 5.07461 15.2501 4.75794Z" fill="black" fillOpacity="0.8" />
            </svg>
          </div>
        </div>
        <div style={{ overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead style={{ backgroundColor: '#e3f2fd' }}>
              <tr style={{ borderBottom: '2px solid rgb(37, 78, 118)' }}>
                <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 600, borderBottom: '2px solid #dee2e6', whiteSpace: 'nowrap', width: '40%' }}>
                  {t('Field') || 'Field'}
                </th>
                <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 600, borderBottom: '2px solid #dee2e6', whiteSpace: 'nowrap' }}>
                  {t('Value') || 'Value'}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={idx} style={{ borderBottom: '1px solid #dee2e6', backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f8f9fa' }}>
                  <td style={{ padding: '12px 8px', fontWeight: 600, color: '#495057', whiteSpace: 'nowrap', textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                    {row.label}
                  </td>
                  <td style={{ padding: '12px 8px', color: row.isError ? '#d32f2f' : '#212529', fontWeight: row.isError ? 600 : 400, textAlign: direction === 'rtl' ? 'right' : 'left', wordBreak: 'break-word' }}>
                    {row.value ?? '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Box>
    </Modal>
  );
};

export default PCNChecksAccordion;